import type { Statut, Version } from "./types";

/** Les mots de la base, en français lisible. */

export const STATUTS: Readonly<Record<Statut, string>> = {
  brouillon: "Brouillon",
  en_revue: "En revue",
  approuvee: "Publiée",
  retiree: "Retirée",
};

export const ACTIONS: Readonly<Record<Version["action"], string>> = {
  import: "Import du dépôt",
  revision: "Révision éditoriale",
  creation: "Création",
  modification: "Correction",
  statut: "Changement de statut",
};

/** Mêmes libellés que l'écran « Signaler » de l'application. */
export const SIGNALEMENTS: Readonly<Record<string, string>> = {
  ambiguous: "La définition est ambiguë",
  other_answer: "Une autre réponse est valable",
  typo: "Il y a une faute",
  inappropriate: "Le contenu est inapproprié",
};

/** Codes d'erreur levés par les fonctions SQL, traduits pour l'administrateur. */
const ERREURS: Readonly<Record<string, string>> = {
  acces_refuse: "Ce compte n'est pas administrateur.",
  // La table du jeu ne se vide jamais : sans mot réservé en base, la recopie
  // n'a rien à écrire (ADR-0006).
  reserve_vide:
    "Aucun mot n'est marqué réservé en base : recolle d'abord supabase/generated/enigmes.sql (docs/SERVEUR.md).",
  session_expiree: "La session a expiré : reconnecte-toi.",
  hors_ligne: "La base est injoignable. Vérifie la connexion et réessaie.",
  entree_introuvable: "Cette entrée n'existe pas.",
  mot_existant: "Ce mot existe déjà dans le corpus (hors entrées retirées).",
  mot_trop_court: "Le mot doit compter au moins quatre lettres.",
  definition_longueur: "La définition doit compter de 5 à 16 mots.",
  definition_circulaire: "La définition contient la réponse.",
  variante_longueur: "Une variante n'a pas la même longueur que le mot.",
  relecture_requise: "Une entrée publiée doit avoir un relecteur.",
  originalite_non_confirmee: "Confirme que la définition est originale avant de publier.",
  facilite_a_confirmer: "Des signaux de facilité doivent être confirmés avant d'écrire.",
  motif_requis: "Dis pourquoi : le motif est obligatoire.",
  statut_invalide: "Statut inconnu.",
  statut_inchange: "L'entrée a déjà ce statut.",
  trop_de_demandes: "Trop de demandes : réessaie dans une minute.",
  envoi_impossible: "Le lien n'a pas pu partir. Réessaie plus tard.",
};

export function messageErreur(code: string): string {
  return ERREURS[code] ?? `Erreur inattendue : ${code}`;
}

const DATE = new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/Paris" });

export function date(iso: string | null): string {
  return iso === null ? "—" : DATE.format(new Date(iso));
}

export function etoiles(moyenne: number | null): string {
  return moyenne === null ? "—" : `${moyenne.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} ★`;
}

/**
 * Ce que devient un changement pour les joueurs, dit juste après l'avoir fait.
 *
 * Un mot libre part tout seul : l'application télécharge le corpus publié et
 * le joue au lancement suivant (ADR-0005, étape 3). Un mot réservé aux parties
 * classées, non : le serveur de jeu ne lit que la table `enigmes`, qu'il faut
 * recopier (ADR-0006). Un brouillon ne part nulle part.
 */
export function suitePourLesJoueurs(
  entree: { readonly statut: Statut; readonly reservee: boolean },
  geste: "correction" | "publication" | "retrait",
): string {
  const fait = { correction: "Correction enregistrée", publication: "Entrée publiée", retrait: "Entrée retirée" }[geste];
  if (geste === "correction" && entree.statut !== "approuvee") return `${fait}.`;
  if (entree.reservee) {
    return `${fait}. Mot réservé aux parties classées : recopie-le vers le jeu (Export et jeu) pour que le serveur ${
      geste === "retrait" ? "cesse de le servir" : "serve cette version"
    }.`;
  }
  return geste === "retrait"
    ? `${fait} : les joueurs ne le recevront plus dès le prochain lancement de l'application.`
    : `${fait} : les joueurs la verront au prochain lancement de l'application, sans nouvelle version.`;
}
